// =====================================================================
// SERVICE: quiz-navigation.service.ts
// FUNCTIONAL OVERVIEW:
//   Entry points used by the app shell (sidebar) for the Quiz feature
//   (JHUD-5314 §6.2): open a new quiz, open a quiz chat from history in
//   read-only mode, and keep the quiz chat history (grouped like the chat
//   history: today, current week, last week, one month ago). The facade
//   calls `refreshRecent()` when a quiz is created or completed.
// DEPENDENCIES: Router, QuizApiService, PlatformRegionService,
//   ChatRegionMarketService, LoggerService
// =====================================================================
import { Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { QuizApiService } from '../../../apis/quiz/quiz-api.service';
import { LoggerService } from '../../../core/services/logger.service';
import { QuizHistoryItem, QuizHistoryResponse } from '../../../models/quiz/quiz-api.models';
import { PlatformRegionService } from '../../../../service/shared/platform-region.service';
import { ChatRegionMarketService } from '../../roleplay/services/chat-region-market.service';

@Injectable({
  providedIn: 'root',
})
export class QuizNavigationService {
  private readonly router = inject(Router);
  private readonly api = inject(QuizApiService);
  private readonly platformRegionService = inject(PlatformRegionService);
  private readonly chatRegionMarketService = inject(ChatRegionMarketService);
  private readonly logger = inject(LoggerService);

  private readonly _recentHistory = signal<QuizHistoryResponse>({});
  private readonly _recentLoading = signal(false);
  private recentRequested = false;

  readonly recentHistory = this._recentHistory.asReadonly();
  readonly recentLoading = this._recentLoading.asReadonly();
  // A chat can be listed in more than one bucket; the flat list keeps it once, newest bucket first.
  readonly recentQuizzes = computed<QuizHistoryItem[]>(() => {
    const history = this._recentHistory();
    const all = [...(history.today ?? []), ...(history.current_week ?? []), ...(history.last_week ?? []), ...(history.one_month_ago ?? [])];
    return all.filter((item, index) => all.findIndex((other) => other.chat_session_id === item.chat_session_id) === index);
  });

  // -----------------------------------------------------------------
  // METHOD PURPOSE:
  //   Opens a fresh quiz. The nonce makes the page reset even when it is
  //   already showing a quiz.
  //
  // MODIFICATION HISTORY (latest first – keep ONLY the last 3):
  //   [JHUD-5314] 2026-10-29 21:00 | Vikas Kumar | History list is loaded by the sidebar, not here.
  //   [JHUD-5941] 2026-10-22 00:00 | Vikas Kumar | Initial implementation.
  // -----------------------------------------------------------------
  openNewQuiz(): Promise<boolean> {
    return this.router.navigate(['/quiz/new'], { queryParams: { new: Date.now() }, skipLocationChange: true });
  }

  // -----------------------------------------------------------------
  // METHOD PURPOSE:
  //   Opens a quiz chat from history on the read-only history page.
  //
  // MODIFICATION HISTORY (latest first – keep ONLY the last 3):
  //   [JHUD-5314] 2026-10-29 21:00 | Vikas Kumar | Open /quiz/history/:chat_session_id.
  //   [JHUD-5941] 2026-10-22 00:00 | Vikas Kumar | Initial implementation.
  // -----------------------------------------------------------------
  openQuiz(chatSessionId: number): Promise<boolean> {
    return this.router.navigate([`/quiz/history/${chatSessionId}`], { skipLocationChange: true });
  }

  // -----------------------------------------------------------------
  // METHOD PURPOSE:
  //   Loads the quiz chat history for the selected business unit and
  //   region. Failures keep the sidebar usable with an empty list.
  //
  // MODIFICATION HISTORY (latest first – keep ONLY the last 3):
  //   [JHUD-5314] 2026-10-29 21:00 | Vikas Kumar | Grouped chat-session history (group_by, business unit, region).
  //   [JHUD-5314] 2026-10-26 12:00 | Vikas Kumar | Use backend `limit` paging.
  //   [JHUD-5941] 2026-10-22 00:00 | Vikas Kumar | Initial implementation.
  // -----------------------------------------------------------------
  loadRecent(): void {
    this.recentRequested = true;
    this._recentLoading.set(true);
    const regionName =  this.platformRegionService.selected_region_code;
    this.api
      .getHistory({
        group_by: 'chat_session',
        business_unit_code: this.platformRegionService.selected_platform_code ?? sessionStorage.getItem('businessUnitCode') ?? '',
        region_name: regionName ?? undefined,
      })
      .subscribe({
        next: (response) => {
          this._recentHistory.set(response ?? {});
          this._recentLoading.set(false);
        },
        error: (error: unknown) => {
          this.logger.error('Recent quizzes failed', { source: 'QuizNavigationService', action: 'loadRecent' }, error);
          this._recentHistory.set({});
          this._recentLoading.set(false);
        },
      });
  }

  // -----------------------------------------------------------------
  // METHOD PURPOSE:
  //   Reloads Recent Quizzes only if the sidebar has shown it already.
  //
  // MODIFICATION HISTORY (latest first – keep ONLY the last 3):
  //   [JHUD-5941] 2026-10-22 00:00 | Vikas Kumar | Initial implementation.
  // -----------------------------------------------------------------
  refreshRecent(): void {
    if (this.recentRequested) {
      this.loadRecent();
    }
  }
}
